import set from 'lodash/set';
import find from 'lodash/find';
import cloneDeep from 'lodash/cloneDeep';
import forEach from 'lodash/forEach';

import { handleActions } from 'redux-actions';

import { getFormValues, SubmissionError } from 'redux-form';

import http from '../../../shared/http';
import endpoints from '../../../../config/endpoints';

import { createSelectOptionsFromSysEnums } from '../../../shared/form-elements/select';

import { actions as rootActions } from  '../../../../modules/root';

export const namespace = 'triggeredMailingsEditor';

const isRocket = process.env.CPA_ENV === 'rocket';

// action types
const FETCH_DATA_SUCCESS = `${namespace}/fetch-data-success`;
const FETCH_DATA_ERROR = `${namespace}/fetch-data-error`;
const SET_INITIAL_STATE = `${namespace}/set-initial-state`;
const TOGGLE_PROGRESS_BAR = `${namespace}/toggle-progress-bar`;
const TOGGLE_TEST_EMAIL_MODAL = `${namespace}/toggle-test-email-modal`;
const FETCH_ADMINS_LOOKUPS_SUCCESS = `${namespace}/fetch-admins-lookups-success`;
const FETCH_ADMINS_LOOKUPS_ERROR = `${namespace}/fetch-admins-lookups-error`;
const SEND_TEST_EMAIL_SUCCESS = `${namespace}/send-test-email-success`;
const SEND_TEST_EMAIL_ERROR = `${namespace}/send-test-email-error`;

const initialData = {
	from: isRocket ? 'RocketProfit <info@rocketprofit.com>' : 'Everad <info@everad.com>',
	language: [{ value: 'ru', label: 'Russian' }],
	templates: [{ id: null, title: '', text: '' }],
	event: []
};

// initial state
const initialState = {
	data: initialData,
	isLoading: true,
	isError: false,
	showTestEmailModal: false,
	testEmailModalIndex: null
};

function getCreateMailingRequest(payload, getState) {
	const values = getFormValues(`${namespace}Form`)(getState());

	return http({
		url: endpoints.MAILINGS_NEW.TRIGGERED_MAILINGS,
		method: 'post',
		actions: [SET_INITIAL_STATE, FETCH_DATA_ERROR],
		data: payload,
		addToResponse: { values }
	});
}

function getUpdateMailingRequest(id, payload, getState) {
	const values = getFormValues(`${namespace}Form`)(getState());

	return http({
		url: `${endpoints.MAILINGS_NEW.TRIGGERED_MAILINGS}/${id}`,
		method: 'put',
		actions: [FETCH_DATA_SUCCESS, FETCH_DATA_ERROR],
		data: payload,
		addToResponse: { values, getState }
	});
}

// actions
export const actions = {
	own: {
		fetchData: props => (dispatch, getState) => {
			const id = props.params.id;

			if (!id) {
				return dispatch({ type: SET_INITIAL_STATE });
			}

			const request = http({
				url: `${endpoints.MAILINGS_NEW.TRIGGERED_MAILINGS}/${id}`,
				method: 'get',
				actions: [FETCH_DATA_SUCCESS, FETCH_DATA_ERROR],
				addToResponse: { getState }
			});

			return request(dispatch);
		},
		onSubmit: (values, _, props) => (dispatch, getState) => {
			dispatch(actions.own.toggleProgressBar());

			const payload = {
				from: values.from,
				language: values.language[0].value,
				templates: values.templates,
				event: values.event[0].value
			};

			const id = props.params.id;

			const request = id
				? getUpdateMailingRequest(id, payload, getState)
				: getCreateMailingRequest(payload, getState);

			return request(dispatch).then((action) => {
				if (action.type === FETCH_DATA_ERROR && action.payload.response.status === 400) {
					throw new SubmissionError(prepareFormErrors(action.payload.response.data));
				}
			});
		},
		toggleProgressBar: () => ({ type: TOGGLE_PROGRESS_BAR }),
		toggleTestEmailModal: () => ({
			type: TOGGLE_TEST_EMAIL_MODAL,
			payload: { index: null }
		}),
		openTestEmailModal: (index) => ({
			type: TOGGLE_TEST_EMAIL_MODAL,
			payload: { index }
		}),
		sendTestEmail: (data) => (dispatch, getState) => {
			const values = getFormValues(`${namespace}Form`)(getState());

			const index = selectors.select(getState(), 'testEmailModalIndex');

			const template = values.templates[index];

			const payload = {
				email: data.email[0].label,
				title: template.title,
				from: values.from,
				language: values.language[0].value,
				text: template.text
			};

			const request = http({
				url: endpoints.MAILINGS_NEW.SEND_TEST,
				method: 'post',
				actions: [SEND_TEST_EMAIL_SUCCESS, SEND_TEST_EMAIL_ERROR],
				data: payload
			});

			dispatch(actions.own.toggleTestEmailModal());

			dispatch(rootActions.spinnerToggle());

			return request(dispatch).then((action) => {
				dispatch(rootActions.spinnerToggle());

				if (action.type === SEND_TEST_EMAIL_ERROR && action.payload.response.status === 400) {
					throw new SubmissionError(prepareFormErrors(action.payload.response.data));
				}
			});
		},
		adminsLookup: text => dispatch => {
			const request = http({
				url: `${endpoints.LOOKUPS.ADMINS}?query=${text}`,
				method: 'get',
				actions: [FETCH_ADMINS_LOOKUPS_SUCCESS, FETCH_ADMINS_LOOKUPS_ERROR]
			});

			return request(dispatch).then((action) => {
				if (action.type === FETCH_ADMINS_LOOKUPS_SUCCESS) {
					return {
						options: createSelectOptionsFromSysEnums(action.payload.data)
					};
				}
			});
		}
	}
};

// reducers
const reducers = Object.assign(
	{
		[FETCH_DATA_SUCCESS]: (state, action) => ({
			...state,
			data: mapResponseDataToStateData(action.payload.data, action.meta),
			isLoading: false,
			isError: false
		}),
		[FETCH_DATA_ERROR]: (state, action) => ({
			...state,
			data: action.meta.values,
			isLoading: false,
			isError: true
		}),
		[SET_INITIAL_STATE]: (state, action) => ({
			...initialState,
			isLoading: false
		}),
		[TOGGLE_PROGRESS_BAR]: (state, action) => ({
			...state,
			isLoading: !state.isLoading
		}),
		[TOGGLE_TEST_EMAIL_MODAL]: (state, action) => ({
			...state,
			showTestEmailModal: !state.showTestEmailModal,
			testEmailModalIndex: action.payload.index
		})
	}
);

// reducers
export default handleActions(reducers, cloneDeep(initialState));

// selectors
export const selectors = {
	select: (state, field) => state[namespace][field],
	getOptions: (state) => {
		return {
			languages: createSelectOptionsFromSysEnums(state.root.lists['languages']),
			events: createSelectOptionsFromSysEnums(state.root.lists['triggered-mailing-event-types'])
		};
	},
	getTestEmailFormInitialValues: (state) => ({
		email: [{
			label: state.root.data.email,
			value: state.root.data.id
		}]
	})
};

function mapResponseDataToStateData(data, { getState }) {
	const state = getState();

	const options = selectors.getOptions(state);

	return {
		from: data.from,
		language: [find(options.languages, { value: data.language })],
		templates: data.templates,
		event: [find(options.events, { value: data.event })]
	};
}

const prepareFormErrors = errors => {
	const validationsErrors = {};
	forEach(errors, value => {
		const key = value.dataPath.slice(1);
		set(validationsErrors, key, value.message);
	});
	return validationsErrors;
};



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/mailings-new/triggered-mailings/editor/index.js